import { useLayoutEffect, type RefObject } from 'react';
import { placePopover, type Rect } from '@/lib/popover';

const GAP = 4;
const MARGIN = 8;

/**
 * 自定义下拉面板的定位：往下还是往上开、最多多高、横向挪多少，才不出可视区（算法在 `lib/popover.ts`）。
 *
 * - `anchorRef` 是面板对齐的参照（外层那个 `relative`）。**面板按屏幕定位（`position: fixed`）**，
 *   上下位置、左边、最大宽高都由这里写到面板的 style 上，className 里别再写 `top-full` / `max-h-*`；
 *   改之前是相对外层 `absolute` 定位 —— 放进带 `overflow-hidden` 的卡片里（「我的」页的语言、
 *   工地设置里的地址候选）就被卡片边裁掉，看着像被挡住。fixed 不受祖先的 overflow 裁剪
 *   （只要祖先没有 transform / filter，本项目的卡片、抽屉都没有）；
 * - `matchWidth`：面板和参照一样宽（下拉、地址候选），className 里的 `w-full` 在 fixed 下不再指外层；
 * - 可视区优先用 `visualViewport`：手机键盘弹出来时它变矮，`innerHeight` 不变；
 * - 打开时、窗口缩放、页面任何一层滚动、`contentKey` 变了（候选条数变了面板变高）都重算；
 *   直接写 DOM 不走 state，打开那一帧不会先出屏再跳回来。
 *
 * 原生 `<select>` 不用它：选项列表是系统画的，自己会摆在屏幕里。
 */
export function usePopoverPlacement(
  open: boolean,
  anchorRef: RefObject<HTMLElement>,
  panelRef: RefObject<HTMLElement>,
  { maxHeight, contentKey, matchWidth }: { maxHeight?: number; contentKey?: unknown; matchWidth?: boolean } = {},
): void {
  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;

    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor || !panel) return;

      const viewport = visibleArea();
      const rect = anchor.getBoundingClientRect();
      panel.style.position = 'fixed';
      if (matchWidth) panel.style.width = `${rect.width}px`;
      // 先按可视区宽度封顶再量宽度；高度用 scrollHeight 量，不用先去掉上限 ——
      // 去掉再加回来会把面板里已经滚下去的位置弹回顶上
      panel.style.maxWidth = `${Math.max(0, viewport.right - viewport.left - MARGIN * 2)}px`;
      const natural = {
        width: panel.offsetWidth,
        height: panel.scrollHeight + (panel.offsetHeight - panel.clientHeight),
      };

      const placement = placePopover({
        anchor: rect,
        panel: natural,
        viewport,
        gap: GAP,
        margin: MARGIN,
        maxHeight,
      });
      // fixed 的 top / bottom 相对窗口：往下开贴着参照的底边，往上开贴着参照的顶边
      panel.style.top = placement.side === 'below' ? `${rect.bottom + GAP}px` : 'auto';
      panel.style.bottom = placement.side === 'above' ? `${window.innerHeight - rect.top + GAP}px` : 'auto';
      panel.style.left = `${rect.left + placement.offsetX}px`;
      panel.style.maxHeight = `${placement.maxHeight}px`;
      panel.style.overflowY = 'auto';
      panel.dataset.side = placement.side;
    };

    // 面板自己里面滚不用重算
    const onScroll = (event: Event) => {
      if (panel && event.target instanceof Node && panel.contains(event.target)) return;
      place();
    };

    place();
    const vv = window.visualViewport;
    window.addEventListener('resize', place);
    // 捕获阶段：页面里任何一层滚动（抽屉、表格外框）都算
    window.addEventListener('scroll', onScroll, true);
    vv?.addEventListener('resize', place);
    vv?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
      vv?.removeEventListener('resize', place);
      vv?.removeEventListener('scroll', place);
    };
  }, [open, anchorRef, panelRef, maxHeight, contentKey, matchWidth]);
}

/** 可视区在布局视口坐标里的范围，和 getBoundingClientRect 同一个坐标系 */
function visibleArea(): Rect {
  const width = document.documentElement.clientWidth;
  const vv = window.visualViewport;
  if (!vv) return { top: 0, left: 0, bottom: window.innerHeight, right: width };
  return {
    top: vv.offsetTop,
    left: vv.offsetLeft,
    bottom: vv.offsetTop + vv.height,
    right: Math.min(width, vv.offsetLeft + vv.width),
  };
}
